import { useState, useEffect, useCallback } from "react";
import { Menu, X, User, Instagram, Facebook, MessageCircle, LogOut, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useChat } from "@/contexts/ChatContext";
import { useQuery } from "@tanstack/react-query";
import { productService } from "@/services/products";
import QuoteListButton from "./quote/QuoteListButton";
import QuoteListDrawer from "./quote/QuoteListDrawer";
import { UserMenu } from "./UserMenu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";

// Alliance logos from Supabase storage - 41 first, then 40
const LOGO_41 = "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/logos-fondos/logos/41.png";
const LOGO_40 = "https://kfieucudibjgtdvyyoey.supabase.co/storage/v1/object/public/logos-fondos/logos/40.png";

// Custom X (Twitter) icon since Lucide doesn't have the new X logo
const XIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Custom TikTok icon
const TikTokIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

// Social media links
const SOCIAL_LINKS = {
  x: "https://x.com/ECONTROLMX",
  facebook: "https://www.facebook.com/EcontrolMX/",
  instagram: "https://www.instagram.com/econtrol.mx?igsh=bHppeGdpcHV0ODc5",
  tiktok: "https://www.tiktok.com/@benjamin_gomez64?_r=1&_t=ZS-9270p5e8mvc",
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isQuoteListOpen, setIsQuoteListOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { openChat } = useChat();

  // Fetch categories for dropdown
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ["categories-nav"],
    queryFn: productService.getCategories,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const handleSignOut = async () => {
    setIsMenuOpen(false);
    await signOut();
  };

  // CSS-based scroll locking to prevent forced reflow
  const toggleBodyScroll = useCallback((lock: boolean) => {
    if (lock) {
      document.documentElement.classList.add('menu-open');
    } else {
      document.documentElement.classList.remove('menu-open');
    }
  }, []);

  // Block body scroll when mobile menu is open
  useEffect(() => {
    toggleBodyScroll(isMenuOpen);
    return () => toggleBodyScroll(false);
  }, [isMenuOpen, toggleBodyScroll]);

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setIsProductsOpen(false);
  }, [location.pathname]);

  const navItems: { name: string; href: string; isChat?: boolean; isProducts?: boolean }[] = [
    { name: "Inicio", href: "/" },
    { name: "Productos", href: "/productos", isProducts: true },
    { name: "Nosotros", href: "/sobre-nosotros" },
    { name: "Blog", href: "/blog" },
    { name: "Contacto", href: "#chat", isChat: true },
  ];

  const handleNavClick = useCallback((href: string) => {
    setIsMenuOpen(false);
    
    // If it's a hash link and we're on the home page
    if (href.startsWith("/#") && location.pathname === "/") {
      const element = document.querySelector(href.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location.pathname]);

  return (
    <>
      <header role="banner" className="fixed top-2 left-2 right-2 sm:top-4 sm:left-4 sm:right-4 z-50">
        <div className="max-w-7xl mx-auto bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80 rounded-full shadow-lg border border-border/30 px-4 sm:px-6">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Alliance Logos - 41 first, then 40 - with explicit dimensions to prevent CLS */}
            <Link to="/" className="flex items-center gap-2 sm:gap-3" aria-label="Ir a la página de inicio">
              <img 
                src={LOGO_41} 
                alt="ECONTROL" 
                className="h-10 sm:h-14 w-auto object-contain" 
                width="112" 
                height="56"
                fetchPriority="high"
              />
              <img 
                src={LOGO_40} 
                alt="MYLVA" 
                className="h-10 sm:h-14 w-auto object-contain" 
                width="112" 
                height="56"
                fetchPriority="high"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1" aria-label="Navegación principal">
              {navItems.map((item) => {
                // Products dropdown
                if (item.isProducts) {
                  return (
                    <NavigationMenu key={item.name}>
                      <NavigationMenuList>
                        <NavigationMenuItem>
                          <NavigationMenuTrigger className="px-3 py-1.5 text-sm font-semibold text-foreground/90 hover:text-foreground bg-transparent hover:bg-accent/50 data-[state=open]:bg-accent/50 rounded-full">
                            {item.name}
                          </NavigationMenuTrigger>
                          <NavigationMenuContent>
                            <ul className="grid gap-1 p-4 w-[300px] bg-popover">
                              <li>
                                <Link
                                  to="/productos"
                                  className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground font-semibold"
                                >
                                  Ver todo el catálogo
                                </Link>
                              </li>
                              <li className="border-t border-border my-1" />
                              {categoriesLoading ? (
                                <>
                                  <li><Skeleton className="h-8 w-full" /></li>
                                  <li><Skeleton className="h-8 w-full" /></li>
                                  <li><Skeleton className="h-8 w-full" /></li>
                                </>
                              ) : (
                                categories?.map((cat) => (
                                  <li key={cat.id}>
                                    <Link
                                      to={`/productos?category=${cat.slug}`}
                                      className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground text-sm"
                                    >
                                      {cat.name}
                                    </Link>
                                  </li>
                                ))
                              )}
                            </ul>
                          </NavigationMenuContent>
                        </NavigationMenuItem>
                      </NavigationMenuList>
                    </NavigationMenu>
                  );
                }

                // Chat button
                if (item.isChat) {
                  return (
                    <button
                      key={item.name}
                      onClick={() => { openChat(); }}
                      className="px-3 py-1.5 text-sm font-semibold text-foreground/90 hover:text-foreground transition-colors duration-200 rounded-full hover:bg-accent/50 whitespace-nowrap flex items-center gap-1.5"
                    >
                      <MessageCircle className="h-4 w-4" />
                      {item.name}
                    </button>
                  );
                }

                // Regular link
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => handleNavClick(item.href)}
                    className="px-3 py-1.5 text-sm font-semibold text-foreground/90 hover:text-foreground transition-colors duration-200 rounded-full hover:bg-accent/50 whitespace-nowrap flex items-center gap-1.5"
                  >
                    {item.name}
                  </Link>
                );
              })}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden lg:flex items-center gap-2">
              <Button size="sm" className="rounded-full" asChild>
                <Link to="/cotizacion">
                  Cotización
                </Link>
              </Button>
              
              <QuoteListButton onClick={() => setIsQuoteListOpen(true)} className="text-foreground hover:bg-accent/50" />

              {/* Social Media Icons */}
              <div className="flex items-center gap-0.5 border-l border-border/50 pl-2 ml-1">
                <a
                  href={SOCIAL_LINKS.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 text-muted-foreground hover:text-primary transition-colors duration-200 rounded-full hover:bg-accent/50"
                  aria-label="Facebook"
                >
                  <Facebook className="h-4 w-4" />
                </a>
                <a
                  href={SOCIAL_LINKS.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 text-muted-foreground hover:text-primary transition-colors duration-200 rounded-full hover:bg-accent/50"
                  aria-label="Instagram"
                >
                  <Instagram className="h-4 w-4" />
                </a>
                <a
                  href={SOCIAL_LINKS.x}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 text-muted-foreground hover:text-primary transition-colors duration-200 rounded-full hover:bg-accent/50"
                  aria-label="X (Twitter)"
                >
                  <XIcon />
                </a>
                <a
                  href={SOCIAL_LINKS.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 text-muted-foreground hover:text-primary transition-colors duration-200 rounded-full hover:bg-accent/50"
                  aria-label="TikTok"
                >
                  <TikTokIcon />
                </a>
              </div>
              
              {user ? (
                <UserMenu />
              ) : (
                <Button variant="ghost" size="icon" asChild className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-accent/50 transition-colors duration-200" aria-label="Iniciar sesión">
                  <Link to="/auth">
                    <User className="h-4 w-4" />
                  </Link>
                </Button>
              )}
            </div>

            <QuoteListDrawer open={isQuoteListOpen} onClose={() => setIsQuoteListOpen(false)} />

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-foreground rounded-full hover:bg-accent/50 transition-all duration-200"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Cerrar menú" : "Abrir menú"}
              aria-expanded={isMenuOpen}
            >
              <div className="relative w-6 h-6">
                <Menu 
                  size={24} 
                  className={`absolute inset-0 transition-all duration-300 ${isMenuOpen ? 'opacity-0 rotate-90 scale-0' : 'opacity-100 rotate-0 scale-100'}`} 
                />
                <X 
                  size={24} 
                  className={`absolute inset-0 transition-all duration-300 ${isMenuOpen ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-0'}`} 
                />
              </div>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      <div 
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${
          isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden={!isMenuOpen}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-background/80 backdrop-blur-sm"
          onClick={() => setIsMenuOpen(false)}
        />
        
        {/* Menu Panel - Fixed positioning with proper overflow handling */}
        <nav 
          aria-label="Menú de navegación móvil"
          className={`absolute top-20 sm:top-24 left-2 sm:left-4 right-2 sm:right-4 bg-background rounded-3xl shadow-xl border border-border/30 overflow-hidden transition-transform duration-300 ease-out ${
            isMenuOpen ? 'translate-y-0' : '-translate-y-4'
          }`}
          style={{ maxHeight: 'calc(100vh - 120px)' }}
        >
          <div className="overflow-y-auto max-h-[inherit] px-4 py-6">
            <div className="flex flex-col space-y-1">
              {navItems.map((item, index) => {
                // Products with collapsible categories
                if (item.isProducts) {
                  return (
                    <Collapsible
                      key={item.name}
                      open={isProductsOpen}
                      onOpenChange={setIsProductsOpen}
                    >
                      <CollapsibleTrigger
                        className="w-full px-4 py-3 text-base font-semibold text-foreground hover:bg-accent/10 rounded-xl transition-all duration-200 text-left flex items-center justify-between animate-fade-in"
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        <span>{item.name}</span>
                        <ChevronDown 
                          className={`h-5 w-5 transition-transform duration-200 ${isProductsOpen ? 'rotate-180' : ''}`} 
                        />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pl-4 space-y-1 mt-1">
                        <Link
                          to="/productos"
                          onClick={() => handleNavClick("/productos")}
                          className="block px-4 py-2.5 text-sm font-medium text-foreground/80 hover:text-foreground hover:bg-accent/10 rounded-lg transition-all duration-200"
                        >
                          Ver todo el catálogo
                        </Link>
                        <div className="border-t border-border/30 my-2" />
                        {categoriesLoading ? (
                          <div className="space-y-2 px-4">
                            <Skeleton className="h-6 w-3/4" />
                            <Skeleton className="h-6 w-2/3" />
                            <Skeleton className="h-6 w-4/5" />
                          </div>
                        ) : (
                          categories?.map((cat) => (
                            <Link
                              key={cat.id}
                              to={`/productos?category=${cat.slug}`}
                              onClick={() => handleNavClick(`/productos?category=${cat.slug}`)}
                              className="block px-4 py-2.5 text-sm text-foreground/70 hover:text-foreground hover:bg-accent/10 rounded-lg transition-all duration-200"
                            >
                              {cat.name}
                            </Link>
                          ))
                        )}
                      </CollapsibleContent>
                    </Collapsible>
                  );
                }

                // Chat button
                if (item.isChat) {
                  return (
                    <button
                      key={item.name}
                      onClick={() => { openChat(); setIsMenuOpen(false); }}
                      className="px-4 py-3 text-base font-semibold text-foreground hover:bg-accent/10 rounded-xl transition-all duration-200 animate-fade-in text-left flex items-center gap-2"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <MessageCircle className="h-5 w-5" />
                      {item.name}
                    </button>
                  );
                }

                // Regular link
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => handleNavClick(item.href)}
                    className="px-4 py-3 text-base font-semibold text-foreground hover:bg-accent/10 rounded-xl transition-all duration-200 animate-fade-in flex items-center gap-2"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </div>
            
            <div className="mt-4 pt-4 border-t border-border/50 space-y-2 animate-fade-in" style={{ animationDelay: '250ms' }}>
              <Button className="w-full" size="default" asChild>
                <Link to="/cotizacion" onClick={() => setIsMenuOpen(false)}>
                  Solicitar Cotización
                </Link>
              </Button>
              
              <Button 
                variant="outline" 
                className="w-full" 
                size="default" 
                onClick={() => { setIsQuoteListOpen(true); setIsMenuOpen(false); }}
              >
                Ver Mi Cotización
              </Button>
              
              {user ? (
                <>
                  <Button variant="outline" className="w-full" size="default" asChild>
                    <Link to="/perfil" onClick={() => setIsMenuOpen(false)}>
                      <User className="h-4 w-4 mr-2" />
                      Mi Perfil
                    </Link>
                  </Button>
                  <Button variant="outline" className="w-full" size="default" asChild>
                    <Link to="/mis-cotizaciones" onClick={() => setIsMenuOpen(false)}>
                      Mis Cotizaciones
                    </Link>
                  </Button>
                  <Button 
                    variant="destructive" 
                    className="w-full" 
                    size="default" 
                    onClick={handleSignOut}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Cerrar Sesión
                  </Button>
                </>
              ) : (
                <Button variant="outline" className="w-full" size="default" asChild>
                  <Link to="/auth" onClick={() => setIsMenuOpen(false)}>
                    <User className="h-4 w-4 mr-2" />
                    Iniciar Sesión
                  </Link>
                </Button>
              )}
            </div>

            {/* Social Media in Mobile Menu */}
            <div className="mt-6 pt-4 border-t border-border/50 pb-2 animate-fade-in" style={{ animationDelay: '300ms' }}>
              <p className="text-sm text-muted-foreground mb-3 text-center">Síguenos en redes</p>
              <div className="flex justify-center gap-3">
                <a
                  href={SOCIAL_LINKS.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 text-muted-foreground hover:text-primary bg-muted/50 rounded-full transition-all duration-200"
                  aria-label="Facebook"
                >
                  <Facebook className="h-5 w-5" />
                </a>
                <a
                  href={SOCIAL_LINKS.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 text-muted-foreground hover:text-primary bg-muted/50 rounded-full transition-all duration-200"
                  aria-label="Instagram"
                >
                  <Instagram className="h-5 w-5" />
                </a>
                <a
                  href={SOCIAL_LINKS.x}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 text-muted-foreground hover:text-primary bg-muted/50 rounded-full transition-all duration-200"
                  aria-label="X (Twitter)"
                >
                  <XIcon className="h-5 w-5" />
                </a>
                <a
                  href={SOCIAL_LINKS.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 text-muted-foreground hover:text-primary bg-muted/50 rounded-full transition-all duration-200"
                  aria-label="TikTok"
                >
                  <TikTokIcon className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
        </nav>
      </div>
    </>
  );
};

export default Header;
